import { FreightType, InPriceStepStage, LandedCostCurrencyMode, MarkupMarginType } from '@/ApiClient';
import { useCurrencyExchangeRates } from '@/store/currency-exchange-rates';
import { assertUnreachable } from '@lib/type-helpers.ts';
import { InPriceStepType } from '@pages/admin/price-configuration/price-configuration-types.ts';
import isNil from 'lodash/isNil';
import round from 'lodash/round';
import type { InPriceStep } from '../hooks/pricing-state';

/**
 * Resolves which currency the landed cost should be in, mirroring the backend resolver.
 */
export const resolveLandedCostCurrencyId = (
  config: { landedCostCurrencyMode?: LandedCostCurrencyMode; landedCostCurrencyId?: number } | undefined,
  costPriceCurrencyId: number
): number => {
  if (config?.landedCostCurrencyMode === LandedCostCurrencyMode.Explicit) {
    return config.landedCostCurrencyId ?? costPriceCurrencyId;
  }
  return costPriceCurrencyId;
};

/**
 * Calculates the markup or margin from outPrice and landedCost with currency conversion support
 * @param outPrice The output price
 * @param landedCost The landed cost
 * @param markupMarginType Whether to calculate as Markup or Margin
 * @param outPriceCurrencyId Currency ID of the output price
 * @param landedCostCurrencyId Currency ID of the landed cost
 * @param seasonId Season ID for exchange rate lookup
 * @param vatPercentageToExclude If provided, divides the out price by (1 + vat/100) before calculating markup or margin
 * @returns The markup multiplier, or undefined if calculation is not possible (including when no
 *          exchange rate exists between the two currencies)
 */
export const calculateMarkupMargin = (
  outPrice: number | undefined,
  landedCost: number | undefined,
  markupMarginType: MarkupMarginType,
  outPriceCurrencyId?: number,
  landedCostCurrencyId?: number,
  seasonId?: number | null,
  vatPercentageToExclude: number = 0
) => {
  if (isNil(outPrice) || isNil(landedCost)) {
    return undefined;
  }

  // Instead of Infinity, return undefined
  if (landedCost === 0) {
    return undefined;
  }

  // If the currencies are set and different, we need to convert the landedCost to the outPrice currency
  if (!isNil(outPriceCurrencyId) && !isNil(landedCostCurrencyId) && outPriceCurrencyId !== landedCostCurrencyId) {
    const converted = useCurrencyExchangeRates().tryConvertCurrency(
      landedCost,
      landedCostCurrencyId,
      outPriceCurrencyId,
      seasonId
    );

    // A brand without a rate for this pair is an ordinary configuration, not an error: this
    // runs while rendering a cell, which renders blank for anything it cannot compute.
    if (isNil(converted)) {
      return undefined;
    }

    landedCost = converted;
  }

  // If VAT should be excluded, divide out price by VAT multiplier first
  let effectiveOutPrice = outPrice;
  if (vatPercentageToExclude > 0) {
    const vatMultiplier = 1 + vatPercentageToExclude / 100;
    effectiveOutPrice = outPrice / vatMultiplier;
  }

  if (markupMarginType === MarkupMarginType.Margin) {
    // Handling edge case to avoid division by zero
    if (effectiveOutPrice === 0) {
      return undefined;
    }
    return ((effectiveOutPrice - landedCost) / effectiveOutPrice) * 100;
  }
  // Default: MarkupMarginType.Markup
  return effectiveOutPrice / landedCost;
};

/**
 * @param costPrice base cost price
 * @param freight freight in either absolute charge or percentage depending on freightType
 * @param freightType freight type: FreightType.Charge (absolute amount) or FreightType.Percentage
 * @param costPriceCurrencyId Required for charge mode if currency conversion is needed
 * @param freightChargeCurrencyId Required for charge mode if currency conversion is needed
 */
export function getAmountAfterFreight(
  costPrice: number | undefined,
  freight: number | undefined,
  freightType: FreightType.Charge,
  costPriceCurrencyId?: number,
  freightChargeCurrencyId?: number
): number | undefined;
export function getAmountAfterFreight(
  costPrice: number | undefined,
  freight: number | undefined,
  freightType: FreightType.Percentage,
  costPriceCurrencyId?: never,
  freightChargeCurrencyId?: never
): number | undefined;
export function getAmountAfterFreight(
  costPrice: number | undefined,
  freight: number | undefined,
  freightType?: FreightType,
  costPriceCurrencyId?: number,
  freightChargeCurrencyId?: number
): number | undefined;
export function getAmountAfterFreight(
  costPrice?: number,
  freight?: number,
  freightType?: FreightType,
  costPriceCurrencyId?: number,
  freightChargeCurrencyId?: number
) {
  if (isNil(costPrice)) {
    return undefined;
  }

  // If freightType is undefined or freight is undefined, bypass freight calculation
  if (isNil(freightType) || isNil(freight)) {
    return costPrice;
  }

  if (freightType === FreightType.Charge) {
    // In charge mode, freight is an absolute amount to add
    let freightAmount = freight;

    // Convert freight charge to cost price currency if needed
    if (
      !isNil(costPriceCurrencyId) &&
      !isNil(freightChargeCurrencyId) &&
      costPriceCurrencyId !== freightChargeCurrencyId
    ) {
      freightAmount = useCurrencyExchangeRates().convertCurrency(freight, freightChargeCurrencyId, costPriceCurrencyId);
    }

    return costPrice + freightAmount;
  }
  // In percentage mode, freight is a percentage
  return costPrice * (1 + freight / 100);
}

/**
 * @param costPrice base cost price
 * @param freight freight in either absolute charge or percentage depending on freightType
 * @param duties duties in percentage 0.4 = 40%
 * @param freightType freight type: FreightType.Charge (absolute amount) or FreightType.Percentage
 * @param costPriceCurrencyId Required for charge mode if currency conversion is needed
 * @param freightChargeCurrencyId Required for charge mode if currency conversion is needed
 */
export function getAmountAfterDuties(
  costPrice: number | undefined,
  freight: number | undefined,
  duties: number | undefined,
  freightType: FreightType.Charge,
  costPriceCurrencyId?: number,
  freightChargeCurrencyId?: number
): number | undefined;
export function getAmountAfterDuties(
  costPrice: number | undefined,
  freight: number | undefined,
  duties: number | undefined,
  freightType: FreightType.Percentage,
  costPriceCurrencyId?: never,
  freightChargeCurrencyId?: never
): number | undefined;
export function getAmountAfterDuties(
  costPrice: number | undefined,
  freight: number | undefined,
  duties: number | undefined,
  freightType?: FreightType,
  costPriceCurrencyId?: number,
  freightChargeCurrencyId?: number
): number | undefined;
export function getAmountAfterDuties(
  costPrice?: number,
  freight?: number,
  duties?: number,
  freightType?: FreightType,
  costPriceCurrencyId?: number,
  freightChargeCurrencyId?: number
) {
  if (isNil(costPrice)) {
    return undefined;
  }

  const amountAfterFreight = getAmountAfterFreight(
    costPrice,
    freight,
    freightType,
    costPriceCurrencyId,
    freightChargeCurrencyId
  );

  if (isNil(amountAfterFreight)) {
    return undefined;
  }

  // If duties is undefined, bypass duties calculation
  if (isNil(duties)) {
    return amountAfterFreight;
  }

  return amountAfterFreight * (1 + duties / 100);
}

/**
 * Converts landed cost to output price by applying markup with currency conversion support
 * @param landedCost The landed cost
 * @param markupMargin The markup multiplier (default: 1)
 * @param markupMarginType Whether to apply as Markup or Margin
 * @param landedCostCurrencyId Currency ID of the landed cost
 * @param outPriceCurrencyId Currency ID of the output price
 * @param seasonId Season ID for exchange rate lookup
 * @param vatPercentageToInclude If provided, multiplies the result by (1 + vat/100) to include VAT
 * @returns The output price, or undefined if landed cost is not provided
 */
export const landedCostToOutPrice = (
  landedCost: number | undefined,
  markupMargin: number = 1,
  markupMarginType: MarkupMarginType,
  landedCostCurrencyId: number | undefined,
  outPriceCurrencyId: number | undefined,
  seasonId: number | undefined,
  vatPercentageToInclude: number = 0
) => {
  if (isNil(landedCost)) {
    return undefined;
  }

  // If the currencies are set and different, we need to convert the landedCost to the outPrice currency
  if (!isNil(outPriceCurrencyId) && !isNil(landedCostCurrencyId) && outPriceCurrencyId !== landedCostCurrencyId) {
    landedCost = useCurrencyExchangeRates().convertCurrency(
      landedCost,
      landedCostCurrencyId,
      outPriceCurrencyId,
      seasonId
    );
  }

  let outPrice: number = 0;
  if (markupMarginType === MarkupMarginType.Margin) {
    outPrice = landedCost / (1 - markupMargin / 100);
  } else if (markupMarginType === MarkupMarginType.Markup) {
    outPrice = landedCost * markupMargin;
  }

  // If VAT should be included in the final out price, multiply by VAT multiplier
  if (vatPercentageToInclude > 0) {
    const vatMultiplier = 1 + vatPercentageToInclude / 100;
    outPrice *= vatMultiplier;
  }

  return outPrice;
};

/**
 * Calculates the target cost price from output price by reversing markup, duties, and freight
 * @param outPrice The output price
 * @param markupTarget The target markup multiplier (default: 1)
 * @param freight The freight in either absolute charge or percentage depending on freightType
 * @param duties The duties percentage
 * @param freightType Freight type: FreightType.Charge or FreightType.Percentage
 * @param outPriceCurrencyId Currency ID of the output price
 * @param costPriceCurrencyId Currency ID of the cost price
 * @param freightChargeCurrencyId Currency ID of the freight charge (required for charge mode)
 * @param seasonId Season ID for exchange rate lookup
 * @param landedCostCurrencyId Currency ID of the landed cost. Defaults to costPriceCurrencyId when omitted.
 * @returns The target cost price, or undefined if output price is not provided
 */
export function outPriceToCostPriceTarget(
  outPrice: number | undefined,
  markupTarget: number | undefined,
  markupMarginType: MarkupMarginType,
  freight: number | undefined,
  duties: number | undefined,
  steps: InPriceStep[],
  freightType: FreightType.Charge,
  outPriceCurrencyId?: number,
  costPriceCurrencyId?: number,
  freightChargeCurrencyId?: number,
  seasonId?: number,
  landedCostCurrencyId?: number
): number | undefined;
export function outPriceToCostPriceTarget(
  outPrice: number | undefined,
  markupTarget: number | undefined,
  markupMarginType: MarkupMarginType,

  freight: number | undefined,
  duties: number | undefined,
  steps: InPriceStep[],
  freightType: FreightType.Percentage,
  outPriceCurrencyId?: never,
  costPriceCurrencyId?: never,
  freightChargeCurrencyId?: never,
  seasonId?: never,
  landedCostCurrencyId?: never
): number | undefined;
export function outPriceToCostPriceTarget(
  outPrice: number | undefined,
  markupTarget: number | undefined,
  markupMarginType: MarkupMarginType,
  freight: number | undefined,
  duties: number | undefined,
  steps: InPriceStep[],
  freightType?: FreightType,
  outPriceCurrencyId?: number,
  costPriceCurrencyId?: number,
  freightChargeCurrencyId?: number,
  seasonId?: number,
  landedCostCurrencyId?: number
): number | undefined;
export function outPriceToCostPriceTarget(
  outPrice: number | undefined,
  markupTarget: number | undefined,
  markupMarginType: MarkupMarginType,
  freight: number = 0,
  duties: number = 0,
  steps: InPriceStep[] = [],
  freightType?: FreightType,
  outPriceCurrencyId?: number,
  costPriceCurrencyId?: number,
  freightChargeCurrencyId?: number,
  seasonId?: number,
  landedCostCurrencyId?: number
) {
  if (isNil(outPrice)) {
    return undefined;
  }

  const effectiveLandedCostCurrencyId = landedCostCurrencyId ?? costPriceCurrencyId;

  // First, convert landed cost from outPrice by reversing markup
  const landedCost = outPriceToLandedCost(
    outPrice,
    markupTarget,
    markupMarginType,
    outPriceCurrencyId,
    effectiveLandedCostCurrencyId,
    seasonId
  );

  if (isNil(landedCost)) {
    return undefined;
  }

  // Then convert landed cost to cost price by removing duties and freight
  return landedCostToCostPrice(
    landedCost,
    freight,
    duties,
    steps,
    freightType,
    freightChargeCurrencyId,
    costPriceCurrencyId,
    seasonId,
    effectiveLandedCostCurrencyId
  );
}

/**
 *
 * @param costPrice base cost price
 * @param freight freight in either absolute charge or percentage depending on freightType
 * @param duties duties in percentage 4 = 4%
 * @param freightType Freight type: FreightType.Charge or FreightType.Percentage
 * @param costPriceCurrencyId Required for charge mode if currency conversion is needed
 * @param freightChargeCurrencyId Required for charge mode if currency conversion is needed
 */
export function costPriceToLandedCost(
  costPrice: number | undefined,
  freight: number | undefined,
  duties: number | undefined,
  steps: InPriceStep[],
  freightType: FreightType.Charge,
  costPriceCurrencyId?: number,
  freightChargeCurrencyId?: number,
  seasonId?: number
): number | undefined;
export function costPriceToLandedCost(
  costPrice: number | undefined,
  freight: number | undefined,
  duties: number | undefined,
  steps: InPriceStep[],
  freightType: FreightType.Percentage,
  costPriceCurrencyId?: never,
  freightChargeCurrencyId?: never,
  seasonId?: never
): number | undefined;
export function costPriceToLandedCost(
  costPrice: number | undefined,
  freight: number | undefined,
  duties: number | undefined,
  steps: InPriceStep[],
  freightType?: FreightType,
  costPriceCurrencyId?: number,
  freightChargeCurrencyId?: number,
  seasonId?: number
): number | undefined;
export function costPriceToLandedCost(
  costPrice?: number,
  freight: number = 0,
  duties: number = 0,
  steps?: InPriceStep[],
  freightType?: FreightType,
  costPriceCurrencyId?: number,
  freightChargeCurrencyId?: number,
  seasonId?: number
) {
  if (isNil(costPrice)) {
    return undefined;
  }

  const costPriceAfterPreFreightSteps = addInPriceSteps(
    costPrice,
    costPriceCurrencyId,
    seasonId,
    steps?.filter((s) => s.stage === InPriceStepStage.PreFreight)
  );

  let costPriceAfterFreight = costPriceAfterPreFreightSteps;

  if (freightType === FreightType.Charge) {
    // In charge mode, freight is an absolute amount to add
    let freightAmount = freight;

    // Convert freight charge to cost price currency if needed
    if (
      !isNil(costPriceCurrencyId) &&
      !isNil(freightChargeCurrencyId) &&
      costPriceCurrencyId !== freightChargeCurrencyId
    ) {
      freightAmount = useCurrencyExchangeRates().convertCurrency(
        freight,
        freightChargeCurrencyId,
        costPriceCurrencyId,
        seasonId
      );
    }

    costPriceAfterFreight += freightAmount;
  } else if (freightType === FreightType.Percentage) {
    // In percentage mode (default), freight is a percentage
    const freightMultiplier = 1 + freight / 100;
    costPriceAfterFreight *= freightMultiplier;
  }

  const costPriceAfterPreDutiesSteps = addInPriceSteps(
    costPriceAfterFreight,
    costPriceCurrencyId,
    seasonId,
    steps?.filter((s) => s.stage === InPriceStepStage.PreDuties)
  );

  // Duties are always applied as percentage
  const dutiesMultiplier = 1 + duties / 100;
  const costPriceAfterDuties = costPriceAfterPreDutiesSteps * dutiesMultiplier;

  return addInPriceSteps(
    costPriceAfterDuties,
    costPriceCurrencyId,
    seasonId,
    steps?.filter((s) => s.stage === InPriceStepStage.PreLandedCost)
  );
}

/**
 * Converts landed cost back to cost price by removing freight and duties
 * @param landedCost The landed cost
 * @param freight The freight in either absolute charge or percentage depending on freightType
 * @param duties The duties percentage
 * @param freightType Freight type: FreightType.Charge or FreightType.Percentage
 * @param freightChargeCurrencyId Currency ID of the freight charge (required for charge mode)
 * @param costPriceCurrencyId Currency ID of the cost price
 * @param seasonId Season ID for exchange rate lookup
 * @param landedCostCurrencyId Currency ID of the landed cost
 * @returns The cost price, or undefined if landed cost is not provided
 */
export function landedCostToCostPrice(
  landedCost: number | undefined,
  freight: number | undefined,
  duties: number | undefined,
  steps: InPriceStep[],
  freightType: FreightType.Charge,
  freightChargeCurrencyId?: number,
  costPriceCurrencyId?: number,
  seasonId?: number,
  landedCostCurrencyId?: number
): number | undefined;
export function landedCostToCostPrice(
  landedCost: number | undefined,
  freight: number | undefined,
  duties: number | undefined,
  steps: InPriceStep[],
  freightType: FreightType.Percentage,
  freightChargeCurrencyId?: never,
  costPriceCurrencyId?: number,
  seasonId?: number,
  landedCostCurrencyId?: number
): number | undefined;
export function landedCostToCostPrice(
  landedCost: number | undefined,
  freight: number | undefined,
  duties: number | undefined,
  steps: InPriceStep[],
  freightType?: FreightType,
  freightChargeCurrencyId?: number,
  costPriceCurrencyId?: number,
  seasonId?: number,
  landedCostCurrencyId?: number
): number | undefined;
export function landedCostToCostPrice(
  landedCost?: number,
  freight: number = 0,
  duties: number = 0,
  steps?: InPriceStep[],
  freightType?: FreightType,
  freightChargeCurrencyId?: number,
  costPriceCurrencyId?: number,
  seasonId?: number,
  landedCostCurrencyId?: number
) {
  if (isNil(landedCost)) {
    return undefined;
  }

  // Convert input from landed cost currency to cost price currency before reversing freight/duties/steps
  if (!isNil(landedCostCurrencyId) && !isNil(costPriceCurrencyId) && landedCostCurrencyId !== costPriceCurrencyId) {
    landedCost = useCurrencyExchangeRates().convertCurrency(
      landedCost,
      landedCostCurrencyId,
      costPriceCurrencyId,
      seasonId
    );
  }

  const landedCostAfterDuties = subtractInPriceStep(
    landedCost,
    costPriceCurrencyId,
    seasonId,
    steps?.filter((s) => s.stage === InPriceStepStage.PreLandedCost).reverse()
  );

  // Remove duties first (always percentage), but skip if duties is undefined/null
  let costPriceAfterFreight = landedCostAfterDuties;
  if (!isNil(duties)) {
    const dutiesMultiplier = 1 + duties / 100;
    costPriceAfterFreight /= dutiesMultiplier;
  }

  const landedCostAfterFreight = subtractInPriceStep(
    costPriceAfterFreight,
    costPriceCurrencyId,
    seasonId,
    steps?.filter((s) => s.stage === InPriceStepStage.PreDuties).reverse()
  );

  let landedCostBeforeFreight = landedCostAfterFreight;
  if (freightType === FreightType.Charge) {
    // In charge mode, freight is an absolute amount to subtract
    let freightAmount = freight ?? 0;

    // Convert freight charge to cost price currency if needed
    if (
      !isNil(costPriceCurrencyId) &&
      !isNil(freightChargeCurrencyId) &&
      costPriceCurrencyId !== freightChargeCurrencyId
    ) {
      freightAmount = useCurrencyExchangeRates().convertCurrency(
        freight,
        freightChargeCurrencyId,
        costPriceCurrencyId,
        seasonId
      );
    }

    landedCostBeforeFreight -= freightAmount;
  } else if (freightType === FreightType.Percentage) {
    // In percentage mode (default), freight is a percentage to divide out
    const freightMultiplier = 1 + freight / 100;
    landedCostBeforeFreight /= freightMultiplier;
  }

  return subtractInPriceStep(
    landedCostBeforeFreight,
    costPriceCurrencyId,
    seasonId,
    steps?.filter((s) => s.stage === InPriceStepStage.PreFreight).reverse()
  );
}

/**
 * Converts a stored value to markup multiplier for calculations.
 * Handles conversion based on preferMarkup setting.
 * @param value The stored value (markup multiplier or margin %)
 * @param preferMarkup Whether the value is stored as markup (true) or margin % (false)
 * @returns The markup multiplier for calculations, or undefined if value is undefined
 */
export const toMarkupForCalculation = (value: number | undefined, preferMarkup: boolean): number | undefined => {
  if (value === undefined) return undefined;
  return preferMarkup ? value : marginToMarkup(value);
};

/**
 * Converts markup to margin percentage
 * Simple conversion: margin = ((markup - 1) / markup) * 100
 * @param markup The markup multiplier
 * @returns The margin percentage, rounded to 4 decimal places
 */
export const markupToMargin = (markup: number) => {
  if (markup <= 0) {
    return 0;
  }
  const margin = ((markup - 1) / markup) * 100;
  return round(margin, 4);
};

/**
 * Converts margin percentage to markup multiplier
 * Simple conversion: markup = 1 / (1 - margin/100)
 * @param marginPercent The margin percentage
 * @returns The markup multiplier, rounded to 4 decimal places
 */
export const marginToMarkup = (marginPercent: number) => {
  if (marginPercent >= 100) {
    return 1;
  }
  const markup = 1 / (1 - marginPercent / 100);
  return round(markup, 4);
};

/**
 * Converts output price to landed cost by reversing the markup with currency conversion support
 * @param outPrice The output price
 * @param markupMargin The markup multiplier (default: 1)
 * @param outPriceCurrencyId Currency ID of the output price
 * @param landedCostCurrencyId Currency ID of the landed cost
 * @param seasonId Season ID for exchange rate lookup
 * @returns The landed cost, or undefined if output price is not provided
 */
export const outPriceToLandedCost = (
  outPrice: number | undefined,
  markupMargin: number = 1,
  markupMarginType: MarkupMarginType,
  outPriceCurrencyId?: number,
  landedCostCurrencyId?: number,
  seasonId?: number,
  vatPercentageToExclude: number = 0
) => {
  if (isNil(outPrice)) {
    return undefined;
  }

  if (!isNil(outPriceCurrencyId) && !isNil(landedCostCurrencyId) && outPriceCurrencyId !== landedCostCurrencyId) {
    outPrice = useCurrencyExchangeRates().convertCurrency(outPrice, outPriceCurrencyId, landedCostCurrencyId, seasonId);
  }

  // Remove VAT from out price (inverse of landedCostToOutPrice which adds VAT)
  if (vatPercentageToExclude > 0) {
    const vatMultiplier = 1 + vatPercentageToExclude / 100;
    outPrice = outPrice / vatMultiplier;
  }
  if (markupMarginType === MarkupMarginType.Margin) {
    return outPrice * (1 - markupMargin / 100);
  }
  // Default: MarkupMarginType.Markup
  return outPrice / markupMargin;
};

function addInPriceSteps(
  amount: number,
  currencyId: number | undefined,
  seasonId: number | undefined,
  steps: InPriceStep[] = []
): number {
  let result = amount;

  for (const step of steps) {
    switch (step.type) {
      case InPriceStepType.Percentage: {
        if (isNil(step.percentage)) {
          continue;
        }

        result *= 1 + step.percentage / 100;
        break;
      }
      case InPriceStepType.Charge: {
        if (isNil(step.charge)) {
          continue;
        }

        let charge = step.charge;
        if (step.currencyId && currencyId && step.currencyId !== currencyId) {
          charge = useCurrencyExchangeRates().convertCurrency(charge, step.currencyId, currencyId, seasonId);
        }

        result += charge;
        break;
      }
      default:
        assertUnreachable(step.type);
    }
  }

  return result;
}

function subtractInPriceStep(
  amount: number,
  currencyId: number | undefined,
  seasonId: number | undefined,
  steps: InPriceStep[] = []
) {
  let result = amount;

  for (const step of steps) {
    switch (step.type) {
      case InPriceStepType.Percentage: {
        if (isNil(step.percentage)) {
          continue;
        }

        result /= 1 + step.percentage / 100;
        break;
      }
      case InPriceStepType.Charge: {
        if (isNil(step.charge)) {
          continue;
        }

        let charge = step.charge;
        if (step.currencyId && currencyId && step.currencyId !== currencyId) {
          charge = useCurrencyExchangeRates().convertCurrency(charge, step.currencyId, currencyId, seasonId);
        }

        result -= charge;
        break;
      }
      default:
        assertUnreachable(step.type);
    }
  }

  return result;
}
